import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  Paper,
  TextField,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  AutoAwesome as GenerateIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useToast } from '../contexts/ToastContext';
import { 
  sendComposeEmail, 
  ComposeEmailRequest,
  searchEmailIds,
  uploadAttachments,
  addAttachmentsToDraft,
  removeAttachmentsFromDraft,
} from '../services/emailService';
import {
  RichTextEditor, 
  MenuControlsContainer,
  MenuDivider,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonStrikethrough,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
  MenuButtonBlockquote,
  MenuButton,
} from 'mui-tiptap';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Mention } from '@tiptap/extension-mention';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Blockquote } from '@tiptap/extension-blockquote';
import { CollapsibleContent } from './extensions/CollapsibleContent';
import { mentionSuggestionOptions } from '../helpers/mentionSuggestionOptions';
import { STORAGE_KEYS, SOCKET_KEYS, API_CONFIG } from '../helpers/constants';
import socketService from '../services/socketService';
import ChannelRestrictionModal from './ChannelRestrictionModal';
import { useSubscriptionValidation } from '../hooks/useSubscriptionValidation';
import CitationsDisplay, { Citation } from './CitationsDisplay';
import DraftUsageMetrics, { DraftUsageMetricsData } from './DraftUsageMetrics';

// File validation constants
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB in bytes
const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.rtf', 
  '.html', '.ics', '.jpg', '.jpeg', '.png', '.gif', '.heic', '.webp', 
  '.mp3', '.mp4', '.zip', '.rar', '.7z', '.pptx', '.ppt'
];

// File validation utility
const validateFile = (file: File): { valid: boolean; error?: string } => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File "${file.name}" exceeds the maximum size of 20 MB`
    };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const lastDotIndex = fileName.lastIndexOf('.');
  
  if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
    return {
      valid: false,
      error: `File "${file.name}" has no valid extension. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`
    };
  }
  
  const fileExtension = fileName.substring(lastDotIndex);
  
  if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return {
      valid: false,
      error: `File type "${fileExtension}" is not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`
    };
  }

  return { valid: true };
};

interface ComposeBoxProps {
  onClose: () => void;
  onSend: (content: string) => void;
  isOpen: boolean;
  isReplyOrForward?: boolean;
  isForward?: boolean; // New prop to distinguish Forward from Reply/Reply All
  channelName?: string;
  emailThreadId?: string;
  channelType?: 'email' | 'message' | 'voice';
  channelId?: string; // Channel ID for Socket.IO verification
  threadId?: string; // Thread ID for Socket.IO verification
  initialData?: {
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    content?: string;
    gmail_message_id?: string;
    gmail_thread_id?: string;
    /** When forwarding, original email attachments to include so recipient receives them */
    attachments?: Array<{
      attachment_id: string;
      filename: string;
      file_type?: string;
      file_size?: number;
    }>;
  };
  emailResponse?: any;
  onGenerateDraft?: () => void;
  isGeneratingDraft?: boolean;
  draftMessageId?: string | null; // Message ID from draft response to delete when sending
  draftCitations?: Citation[] | null; // Citations (Sources) from generate draft main_chat response
  draftUsageMetrics?: DraftUsageMetricsData | null; // Confidence score + token usage from main_chat
}

const ComposeBox: React.FC<ComposeBoxProps> = ({ onClose, onSend, isOpen, isReplyOrForward = false, isForward = false, channelName, emailThreadId, channelType = 'email', channelId, threadId, initialData, emailResponse, onGenerateDraft, isGeneratingDraft = false, draftMessageId, draftCitations = null, draftUsageMetrics = null }) => {
  const muiTheme = useTheme();
  const { showSuccess, showError } = useToast();
  
  // Subscription validation hook for attachment restrictions
  const { validate: validateSubscription } = useSubscriptionValidation();
  
  // State for attachment restriction modal
  const [openAttachmentRestrictionModal, setOpenAttachmentRestrictionModal] = useState<boolean>(false);
  const [composeContent, setComposeContent] = useState<string>('');
  const [isEditorFocused, setIsEditorFocused] = useState<boolean>(false);
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [toInputValue, setToInputValue] = useState<string>('');
  const [ccInputValue, setCcInputValue] = useState<string>('');
  const [bccInputValue, setBccInputValue] = useState<string>('');
  const [showCc, setShowCc] = useState<boolean>(false);
  const [showBcc, setShowBcc] = useState<boolean>(false);
  // Refs to track when we're programmatically adding emails (to prevent duplication)
  const isAddingToEmailRef = useRef<boolean>(false);
  const isAddingCcEmailRef = useRef<boolean>(false);
  const isAddingBccEmailRef = useRef<boolean>(false);
  const [subject, setSubject] = useState<string>('');
  const [editorKey, setEditorKey] = useState<number>(0);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState<boolean>(false);
  
  // Attachment state
  const [attachments, setAttachments] = useState<Array<{
    file?: File;
    id?: string;
    filename?: string;
    file_size?: number;
    file_type?: string;
    uploadStatus: 'pending' | 'uploading' | 'success' | 'error';
    error?: string;
  }>>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState<boolean>(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<any>(null);
  const previousContentRef = useRef<string>('');
  const [draftStatusMessage, setDraftStatusMessage] = useState<string>('');
  const [draftStatusType, setDraftStatusType] = useState<'loading' | 'success' | 'error'>('loading');
  const socketHandlerRef = useRef<((eventData: any) => void) | null>(null);
  const isGeneratingDraftRef = useRef<boolean>(false);
  // Refs for is_draft_edited: store generated draft content to detect user edits before send
  const originalDraftContentRef = useRef<string | null>(null);
  const hasStoredOriginalDraftRef = useRef<boolean>(false);

  // Email address suggestions for To/Cc/Bcc autocomplete (from POST /email/emailIds/search)
  const [emailSuggestions, setEmailSuggestions] = useState<string[]>([]);
  const [emailSuggestionsLoading, setEmailSuggestionsLoading] = useState<boolean>(false);
  const emailSuggestionsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch email suggestions for To/Cc/Bcc; only when email channel and channelId present
  const fetchEmailSuggestions = useCallback(async (search: string) => {
    if (channelType !== 'email' || !channelId) {
      setEmailSuggestions([]);
      return;
    }
    setEmailSuggestionsLoading(true);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const res = await searchEmailIds(channelId, search.trim(), token);
      setEmailSuggestions(Array.isArray(res.emails) ? res.emails : []);
    } catch {
      setEmailSuggestions([]);
    } finally {
      setEmailSuggestionsLoading(false);
    }
  }, [channelId, channelType]);

  // Debounced search so we don't hit the API on every keystroke
  const debouncedFetchEmailSuggestions = useCallback((search: string) => {
    if (emailSuggestionsDebounceRef.current) {
      clearTimeout(emailSuggestionsDebounceRef.current);
      emailSuggestionsDebounceRef.current = null;
    }
    if (channelType !== 'email' || !channelId) return;
    emailSuggestionsDebounceRef.current = setTimeout(() => {
      emailSuggestionsDebounceRef.current = null;
      fetchEmailSuggestions(search);
    }, 280);
  }, [channelId, channelType, fetchEmailSuggestions]);

  // Clear debounce timer on unmount
  useEffect(() => {
    return () => {
      if (emailSuggestionsDebounceRef.current) {
        clearTimeout(emailSuggestionsDebounceRef.current);
        emailSuggestionsDebounceRef.current = null;
      }
    };
  }, []);

  // Extract email from channel name
  const extractEmailFromChannelName = (channelName: string): string | null => {
    // Look for email pattern in the channel name
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
    const match = channelName.match(emailRegex);
    return match ? match[1] : null;
  };

  const extractedEmail = channelName ? extractEmailFromChannelName(channelName) : null;

  // Get logged-in user ID from localStorage
  const getLoggedInUserId = (): string | null => {
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (storedUserData) {
        const parsedData = JSON.parse(storedUserData);
        return parsedData.user_id || null;
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return null;
  };

  // Update ref when isGeneratingDraft changes
  useEffect(() => {
    isGeneratingDraftRef.current = isGeneratingDraft;
  }, [isGeneratingDraft]);

  // Socket.IO listener for draft generation status
  // Connect socket when compose box opens (for reply/forward) to avoid missing status messages
  useEffect(() => {
    // Only set up socket listener when compose box is open and it's a reply/forward
    if (!isOpen || !isReplyOrForward || !channelId || !threadId) {
      // Clean up if compose box is closed or not a reply/forward
      if (socketHandlerRef.current) {
        socketService.off('shay_realtime', socketHandlerRef.current);
        socketHandlerRef.current = null;
      }
      setDraftStatusMessage('');
      setDraftStatusType('loading');
      return;
    }

    // Create socket event handler
    const handleSocketEvent = (eventData: any) => {
      // Only process events when draft generation is in progress (use ref to get current value)
      if (!isGeneratingDraftRef.current) {
        return;
      }

      // Check if this is a draft generation status message (type 23)
      if (eventData.type === SOCKET_KEYS.DRAFT_GENRATE_STATUS && eventData.data) {
        console.log('🔔 shay_realtime event received in Draft Status:', eventData);
        const data = eventData.data;
        const { channelId: msgChannelId, threadId: msgThreadId, userId: msgUserId } = data;
        const loggedInUserId = getLoggedInUserId();

        // Verify channelId, threadId, and userId match
        if (
          msgChannelId === channelId &&
          msgThreadId === threadId &&
          msgUserId === loggedInUserId
        ) {
          // Extract status information from data.data
          // Expected structure: { task_id, status, draft_id, error_message, timestamp, metadata: { stage, message } }
          const statusData = data.data;
          
          if (statusData) {
            let statusMessage = '';
            let statusType: 'loading' | 'success' | 'error' = 'loading';
            
            // Get message from metadata.message (dynamic status from server)
            if (statusData.metadata && statusData.metadata.message) {
              statusMessage = statusData.metadata.message;
            }
            // Show error message if present
            else if (statusData.error_message) {
              statusMessage = statusData.error_message;
              statusType = 'error';
            }
            
            // Determine status type based on status field
            if (statusData.status === 'completed' || statusData.status === 'success') {
              statusType = 'success';
            } else if (statusData.status === 'failed' || statusData.status === 'error') {
              statusType = 'error';
            } else if (statusData.status === 'processing' || statusData.status === 'in_progress') {
              statusType = 'loading';
            }
            
            // Update the status message and type in UI
            if (statusMessage) {
              setDraftStatusMessage(statusMessage);
              setDraftStatusType(statusType);
            }
          }
        }
      }
    };

    // Connect to socket and set up listener when compose box opens
    const setupSocketListener = async () => {
      try {
        // Remove any existing handler first to prevent duplicates
        if (socketHandlerRef.current) {
          socketService.off('shay_realtime', socketHandlerRef.current);
          socketHandlerRef.current = null;
        }

        // Connect to socket if not already connected
        const socket = socketService.connect(API_CONFIG.SOCKET_URL);
        if (!socket) {
          console.error("Failed to get socket instance");
          return;
        }

        // Wait for connection to be established (increased timeout for production)
        const isConnected = await socketService.waitForConnection(15000);
        if (!isConnected) {
          console.error("Socket connection timeout");
          return;
        }

        // Store handler reference for cleanup
        socketHandlerRef.current = handleSocketEvent;

        // Listen for shay_realtime events
        socketService.on('shay_realtime', handleSocketEvent);
        
        console.log('✅ Socket connected and listener set up for draft generation');
      } catch (error) {
        console.error("Error setting up socket listener:", error);
      }
    };

    setupSocketListener();

    // Cleanup function
    return () => {
      // Clean up when compose box closes or conditions change
      if (socketHandlerRef.current) {
        console.log('🧹 Cleaning up socket listener for compose box');
        socketService.off('shay_realtime', socketHandlerRef.current);
        socketHandlerRef.current = null;
        setDraftStatusMessage('');
        setDraftStatusType('loading');
      }
    };
  }, [isOpen, isReplyOrForward, channelId, threadId]);

  // Populate form with initial data when provided
  useEffect(() => {
    if (initialData) {
      if (initialData.to && initialData.to.length > 0) setToEmails(initialData.to);
      if (initialData.cc && initialData.cc.length > 0) {
        setCcEmails(initialData.cc);
        setShowCc(true);
      }
      if (initialData.bcc && initialData.bcc.length > 0) {
        setBccEmails(initialData.bcc);
        setShowBcc(true);
      }
      if (initialData.subject) setSubject(initialData.subject);
      // Pre-populate attachments when forwarding so original email attachments are included (only when empty so we don't overwrite user-added files)
      if (initialData.attachments && initialData.attachments.length > 0) {
        setAttachments((prev) =>
          prev.length === 0
            ? initialData.attachments!.map((att) => ({
                id: att.attachment_id,
                filename: att.filename,
                file_size: att.file_size,
                file_type: att.file_type,
                uploadStatus: 'success' as const,
              }))
            : prev
        );
      }
      if (initialData.content) {
        // When this is a reply/forward with a generated draft (draftMessageId set), store content as original draft for is_draft_edited comparison
        if (isReplyOrForward && draftMessageId && !hasStoredOriginalDraftRef.current) {
          originalDraftContentRef.current = initialData.content;
          hasStoredOriginalDraftRef.current = true;
        }
        // Only update if content has actually changed to prevent unnecessary re-renders
        // that could cause formatting loss
        if (previousContentRef.current !== initialData.content) {
          previousContentRef.current = initialData.content;
          setComposeContent(initialData.content);
          // Update editor content directly if editor is already created
          // This preserves formatting better than forcing a re-render
          if (editorRef.current) {
            // Check if editor view is available before trying to access it
            try {
              const editor = editorRef.current;
              // Verify editor view is mounted and available
              if (editor && editor.view && editor.view.dom) {
                const currentContent = editor.getHTML();
                // Only update if content is different to avoid losing formatting
                if (currentContent !== initialData.content) {
                  // Use requestAnimationFrame to ensure the update happens after the editor is fully ready
                  requestAnimationFrame(() => {
                    if (editorRef.current && editorRef.current.view && editorRef.current.view.dom) {
                      try {
                        editorRef.current.commands.setContent(initialData.content, { emitUpdate: false });
                        // For reply/reply-all/forward, position cursor at top after content is set
                        // (setContent often leaves cursor at end; focus('start') is the correct API)
                        if (isReplyOrForward) {
                          setTimeout(() => {
                            try {
                              if (editorRef.current?.view?.dom) {
                                editorRef.current.commands.focus('start');
                              }
                            } catch (err) {
                              console.warn('Failed to set cursor position after content update:', err);
                            }
                          }, 150);
                        }
                      } catch (error) {
                        // If setting content fails, force re-render as fallback
                        console.warn('Failed to set editor content directly, forcing re-render:', error);
                        setEditorKey(prev => prev + 1);
                      }
                    }
                  });
                }
              } else {
                // Editor view not ready, force re-render
                setEditorKey(prev => prev + 1);
              }
            } catch (error) {
              // If editor access fails, force re-render as fallback
              console.warn('Editor not ready, forcing re-render:', error);
              setEditorKey(prev => prev + 1);
            }
          } else {
            // If editor doesn't exist yet, force re-render (editorKey will be used in onCreate)
            setEditorKey(prev => prev + 1);
          }
        }
      } else if (initialData && !initialData.content) {
        // Reset previous content ref if initialData exists but has no content
        previousContentRef.current = '';
      }
    }
  }, [initialData, isReplyOrForward, draftMessageId]);

  // Sync composeContent with editor when editor is ready (only for external content updates)
  // This handles cases where composeContent is updated from initialData but editor wasn't ready
  useEffect(() => {
    if (editorRef.current && composeContent && previousContentRef.current === composeContent) {
      // Only sync if this content matches what we expect from initialData
      // This prevents syncing user edits back to the editor
      requestAnimationFrame(() => {
        try {
          if (editorRef.current && editorRef.current.view && editorRef.current.view.dom) {
            const currentContent = editorRef.current.getHTML();
            // Only update if content is significantly different (more than whitespace)
            const normalizedCurrent = currentContent.trim();
            const normalizedNew = composeContent.trim();
            if (normalizedCurrent !== normalizedNew && normalizedNew.length > 0) {
              editorRef.current.commands.setContent(composeContent, { emitUpdate: false });
              // For reply/reply-all/forward, position cursor at top after syncing content
              if (isReplyOrForward) {
                setTimeout(() => {
                  try {
                    if (editorRef.current?.view?.dom) {
                      editorRef.current.commands.focus('start');
                    }
                  } catch (err) {
                    console.warn('Failed to set cursor position after sync:', err);
                  }
                }, 150);
              }
            }
          }
        } catch (error) {
          // Silently handle errors - editor might not be ready yet
          // This is expected during initial mount
        }
      });
    }
  }, [composeContent, isReplyOrForward]);

  // When compose opens for reply/reply-all/forward, ensure cursor is at start after content has settled
  // Defer until editor view is mounted (Tiptap view.dom not available immediately)
  const replyForwardOpenTimeRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isOpen || !isReplyOrForward) {
      replyForwardOpenTimeRef.current = null;
      return;
    }
    replyForwardOpenTimeRef.current = Date.now();
    const t2 = setTimeout(() => {
      try {
        const editor = editorRef.current;
        if (!editor || replyForwardOpenTimeRef.current === null) return;
        // Access view only inside try/catch; focus() may throw if view not mounted yet
        editor.commands.focus('start');
      } catch (err) {
        console.warn('Failed to set cursor to start on reply/forward open:', err);
      }
    }, 1500);
    return () => clearTimeout(t2);
  }, [isOpen, isReplyOrForward]);

  const handleSend = async () => {

    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';

    // Validation for To field
    if (toEmails.length === 0) {
      showError('Please add at least one recipient in the To field');
      return;
    }

    // Validation for editor content
    if (!composeContent.replace(/<[^>]*>/g, '').trim()) {
      showError('Please enter a message before sending');
      return;
    }

    const fromEmail = extractedEmail || '';
    if (!fromEmail || !token) {
      showError('No email available for sending');
      return;
    }

    // Check for failed uploads
    const hasFailedUploads = attachments.some(a => a.uploadStatus === 'error');
    if (hasFailedUploads) {
      showError('Please remove failed attachments or retry uploading before sending');
      return;
    }

    setIsSending(true);

    try {
      // Step 1: Upload any pending attachments first
      let attachmentIds: string[] = [];
      if (attachments.length > 0) {
        try {
          attachmentIds = await uploadPendingAttachments();
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to upload attachments';
          showError(errorMessage);
          setIsSending(false);
          return;
        }
      }

      // Step 2: Send compose email with attachment IDs
      // Compare current content to original generated draft to set is_draft_edited (reply/forward only)
      const isDraftEdited =
        isReplyOrForward &&
        draftMessageId &&
        originalDraftContentRef.current != null
          ? (() => {
              const orig = (originalDraftContentRef.current ?? '').trim();
              const curr = composeContent.trim();
              return orig !== curr;
            })()
          : false;

      // Preserve blank lines: every empty paragraph (with or without attrs) becomes one <br> so 1, 2, 3… blanks
      // render consistently and match the single line after "Hi Team," (no full <p> block = no extra margin)
      const normalizeHtmlLineBreaks = (html: string): string =>
        html.replace(/<p[^>]*>\s*<\/p>/gi, '<br>');

      const contentToSend = normalizeHtmlLineBreaks(composeContent);

      const emailData: ComposeEmailRequest = {
        from_email: fromEmail,
        to_emails: toEmails,
        cc_emails: ccEmails,
        bcc_emails: bccEmails,
        subject: subject,
        content: contentToSend,
        content_type: 'text/html',
        gmail_message_id: initialData?.gmail_message_id,
        // Use emailThreadId for replies, otherwise use initialData gmail_thread_id
        gmail_thread_id: isReplyOrForward && emailThreadId ? emailThreadId : initialData?.gmail_thread_id,
        attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
        delete_message_id: draftMessageId || '', // Pass draft message_id if available, otherwise empty string
        is_draft_edited: isDraftEdited,
      };

      const response = await sendComposeEmail(emailData, token);
      
      if (response.success) {
        // Show success notification
        showSuccess('Email sent successfully!');
        
        // Reset form first
        setComposeContent('');
        setEditorKey(0);
        setToEmails([]);
        setCcEmails([]);
        setBccEmails([]);
        setToInputValue('');
        setCcInputValue('');
        setBccInputValue('');
        setSubject('');
        setShowCc(false);
        setShowBcc(false);
        setAttachments([]);
        setDraftId(null);
        onClose();
        
        // Call the onSend callback for thread list refresh (after UI reset)
        onSend(composeContent);
      } else {
        const errorMessage = response.message || 'Failed to send email';
        showError(errorMessage);
      }
    } catch (error) {
      console.error('Send error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email';
      showError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  // File selection handler
  // Attachment upload validation handler (called after files selected to preserve user activation for file picker)
  const handleAttachmentValidation = async (): Promise<boolean> => {
    try {
      // Pass channelId when inside a channel for subscription validation
      const validationData = await validateSubscription(channelId ? { channelId } : {});
      if (validationData && !validationData.canCreateAttachment) {
        setOpenAttachmentRestrictionModal(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error validating subscription for attachment:', error);
      return true; // Fail open
    }
  };

  // Open file picker synchronously so browser allows it (user activation required)
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Capture file list as array BEFORE clearing the input; clearing value can invalidate event.target.files in some browsers
    const rawFiles = event.target.files;
    const filesArray = rawFiles && rawFiles.length > 0 ? Array.from(rawFiles) : [];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (filesArray.length === 0) return;

    const validFiles: Array<{
      file: File;
      filename: string;
      file_size: number;
      file_type: string;
      uploadStatus: 'pending';
    }> = [];
    const invalidFiles: Array<{ name: string; error: string }> = [];

    filesArray.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push({
          file,
          filename: file.name,
          file_size: file.size,
          file_type: file.type,
          uploadStatus: 'pending' as const,
        });
      } else {
        invalidFiles.push({ name: file.name, error: validation.error || 'Invalid file' });
      }
    });

    if (invalidFiles.length > 0) {
      invalidFiles.forEach(({ name, error }) => {
        showError(`${name}: ${error}`);
      });
    }

    // Add files to state immediately so they are visible in the UI
    if (validFiles.length > 0) {
      setAttachments(prev => [...prev, ...validFiles]);
      // Then validate subscription; if not allowed, remove the just-added files and show modal
      try {
        const canProceed = await handleAttachmentValidation();
        if (!canProceed) {
          setAttachments(prev => prev.slice(0, prev.length - validFiles.length));
          setOpenAttachmentRestrictionModal(true);
        }
      } catch (_) {
        // On validation error, leave attachments visible (fail open)
      }
    }
  };

  // Remove attachment
  const handleRemoveAttachment = async (index: number) => {
    const attachment = attachments[index];
    
    // If attachment is associated with a draft and has an ID, remove it from draft
    if (draftId && attachment.id) {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        await removeAttachmentsFromDraft(draftId, { attachment_ids: [attachment.id] }, token);
      } catch (error) {
        console.error('Failed to remove attachment from draft:', error);
        // Continue with local removal even if draft removal fails
      }
    }

    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Upload attachments
  const uploadPendingAttachments = async (): Promise<string[]> => {
    const pendingAttachments = attachments.filter(a => a.uploadStatus === 'pending' && a.file);
    
    if (pendingAttachments.length === 0) {
      // Return IDs of already uploaded attachments
      return attachments.filter(a => a.id).map(a => a.id!);
    }

    setIsUploadingAttachments(true);
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';

    try {
      // Update status to uploading
      setAttachments(prev => prev.map(a => 
        a.uploadStatus === 'pending' ? { ...a, uploadStatus: 'uploading' as const } : a
      ));

      const filesToUpload = pendingAttachments.map(a => a.file!).filter(Boolean);

      // New compose: do not pass thread_id. Reply/reply-all/forward: pass thread_id so API can associate attachments with thread
      const uploadResponse = await uploadAttachments(
        {
          files: filesToUpload,
          draft_email_id: draftId || undefined,
          ...(isReplyOrForward && threadId ? { thread_id: threadId } : {}),
          channel_id: channelId,
        },
        token
      );

      // Get attachment IDs from response
      // Handle new API format: { success: true, attachments: [...] }
      const uploadedIds = uploadResponse.attachments && uploadResponse.attachments.length > 0
        ? uploadResponse.attachments.map(att => att.attachment_id)
        : (uploadResponse.attachment_ids || (uploadResponse.attachment_id ? [uploadResponse.attachment_id] : []));

      // Update attachments with IDs
      setAttachments(prev => {
        const updated = [...prev];
        let idIndex = 0;
        
        for (let i = 0; i < updated.length; i++) {
          if (updated[i].uploadStatus === 'uploading' && idIndex < uploadedIds.length) {
            updated[i] = {
              ...updated[i],
              id: uploadedIds[idIndex],
              uploadStatus: 'success' as const,
            };
            idIndex++;
          }
        }
        
        return updated;
      });

      // If draft exists, associate attachments with draft
      if (draftId && uploadedIds.length > 0) {
        try {
          await addAttachmentsToDraft(
            draftId,
            {
              attachment_ids: uploadedIds,
              ...(isReplyOrForward && threadId ? { thread_id: threadId } : {}),
            },
            token
          );
        } catch (error) {
          console.error('Failed to add attachments to draft:', error);
          // Don't fail the upload if draft association fails
        }
      }

      // Return all attachment IDs (both newly uploaded and existing)
      const allIds = attachments
        .filter(a => a.id)
        .map(a => a.id!)
        .concat(uploadedIds);
      
      return allIds;
    } catch (error) {
      console.error('Upload error:', error);
      
      // Mark failed attachments
      setAttachments(prev => prev.map(a => 
        a.uploadStatus === 'uploading' 
          ? { ...a, uploadStatus: 'error' as const, error: error instanceof Error ? error.message : 'Upload failed' }
          : a
      ));

      throw error;
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Check if there's any content that would be lost
  const hasContent = () => {
    return (
      composeContent.trim() !== '' ||
      toEmails.length > 0 ||
      ccEmails.length > 0 ||
      bccEmails.length > 0 ||
      subject.trim() !== '' ||
      attachments.length > 0
    );
  };

  // Handle close with confirmation
  const handleCloseWithConfirmation = () => {
    if (hasContent()) {
      setShowCloseConfirmation(true);
    } else {
      handleClose();
    }
  };

  // Handle actual close after confirmation
  const handleClose = () => {
    // Clean up socket listener when compose box closes
    if (socketHandlerRef.current) {
      socketService.off('shay_realtime', socketHandlerRef.current);
      socketHandlerRef.current = null;
    }
    
    setComposeContent('');
    setEditorKey(prev => prev + 1); // Force re-render to reset editor
    editorRef.current = null; // Clear editor reference
    setToEmails([]);
    setCcEmails([]);
    setBccEmails([]);
    setToInputValue('');
    setCcInputValue('');
    setBccInputValue('');
    setSubject('');
    setShowCc(false);
    setShowBcc(false);
    setAttachments([]);
    setDraftId(null);
    setShowCloseConfirmation(false);
    setDraftStatusMessage(''); // Clear draft status message on close
    setDraftStatusType('loading'); // Reset status type on close
    // Clear draft-comparison refs so next open gets a fresh snapshot
    originalDraftContentRef.current = null;
    hasStoredOriginalDraftRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Handle cancel (same as close)
  const handleCancel = () => {
    handleCloseWithConfirmation();
  };

  // Handle confirmation dialog actions
  const handleConfirmClose = () => {
    handleClose();
  };

  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Helper function to add email if valid
  const addEmailIfValid = (
    value: string,
    emails: string[],
    setEmails: (emails: string[]) => void,
    clearInput?: () => void
  ): boolean => {
    const trimmedValue = value.trim();
    if (trimmedValue && isValidEmail(trimmedValue) && !emails.includes(trimmedValue)) {
      setEmails([...emails, trimmedValue]);
      if (clearInput) {
        clearInput();
      }
      return true;
    }
    return false;
  };

  const handleEmailInput = (
    inputValue: string,
    setInputValue: (value: string) => void,
    emails: string[],
    setEmails: (emails: string[]) => void,
    isAddingRef: React.MutableRefObject<boolean>
  ) => {
    return (event: any, newValue: string[]) => {
      // If we're programmatically adding emails, don't process onChange
      // This prevents duplication when we add emails via keydown/blur handlers
      if (isAddingRef.current) {
        isAddingRef.current = false;
        return;
      }
      
      // Autocomplete's onChange provides an array of selected values (chips/tags)
      const trimmedInput = inputValue.trim();
      
      // Remove duplicates using Set
      let uniqueEmails = Array.from(new Set(newValue));
      
      // Filter out the current input value if it's a valid email
      // This prevents the email from appearing both as a chip and as text
      if (trimmedInput && isValidEmail(trimmedInput)) {
        // If input value is in the array, remove it and clear the input
        if (uniqueEmails.includes(trimmedInput)) {
          uniqueEmails = uniqueEmails.filter(email => email !== trimmedInput);
          setInputValue('');
        }
      }
      
      // Set emails - this will update the chips
      setEmails(uniqueEmails);
    };
  };

  const handleEmailKeyDown = (
    inputValue: string,
    setInputValue: (value: string) => void,
    emails: string[],
    setEmails: (emails: string[]) => void,
    isAddingRef: React.MutableRefObject<boolean>
  ) => {
    return (event: React.KeyboardEvent) => {
      const value = inputValue.trim();
      
      // Handle Enter, Space, or Comma
      if (event.key === 'Enter' || event.key === ' ' || event.key === ',') {
        // Check if value contains multiple emails separated by commas
        if (value.includes(',')) {
          const emailParts = value.split(',').map(e => e.trim()).filter(e => e);
          const validEmails: string[] = [];
          
          emailParts.forEach(part => {
            if (isValidEmail(part) && !emails.includes(part) && !validEmails.includes(part)) {
              validEmails.push(part);
            }
          });
          
          if (validEmails.length > 0) {
            // Mark that we're programmatically adding emails
            isAddingRef.current = true;
            // Clear input first
            setInputValue('');
            // Then add emails
            setEmails([...emails, ...validEmails]);
          } else {
            // Keep only invalid/incomplete parts
            const invalidParts = emailParts.filter(part => !isValidEmail(part));
            setInputValue(invalidParts.join(', ') || '');
          }
        } else if (value && isValidEmail(value) && !emails.includes(value)) {
          // Single valid email - mark as programmatic add, clear input, then add email
          isAddingRef.current = true;
          setInputValue('');
          setEmails([...emails, value]);
        } else if (value && !isValidEmail(value)) {
          // Invalid email - clear it
          setInputValue('');
        }
        event.preventDefault();
      }
    };
  };

  // Handle input change - track the input value, process comma-separated emails immediately
  const handleEmailInputChange = (
    inputValue: string,
    setInputValue: (value: string) => void,
    emails: string[],
    setEmails: (emails: string[]) => void,
    isAddingRef: React.MutableRefObject<boolean>,
    onSearchChange?: (search: string) => void
  ) => {
    return (event: any, newInputValue: string) => {
      // Check if the input contains a comma (multiple emails) - process immediately
      if (newInputValue.includes(',')) {
        const emailParts = newInputValue.split(',').map(e => e.trim()).filter(e => e);
        const validEmails: string[] = [];
        
        emailParts.forEach(part => {
          if (isValidEmail(part) && !emails.includes(part) && !validEmails.includes(part)) {
            validEmails.push(part);
          }
        });
        
        if (validEmails.length > 0) {
          // Mark as programmatic add to prevent onChange from processing
          isAddingRef.current = true;
          // Clear input for processed emails and add them
          const lastPart = emailParts[emailParts.length - 1];
          const valueToShow = isValidEmail(lastPart) ? '' : lastPart;
          setInputValue(valueToShow);
          setEmails([...emails, ...validEmails]);
          onSearchChange?.(valueToShow);
        } else {
          // No valid emails, keep the input as is
          setInputValue(newInputValue);
          onSearchChange?.(newInputValue);
        }
      } else {
        // Just track the input value, don't validate yet (validation happens on blur/keydown)
        setInputValue(newInputValue);
        onSearchChange?.(newInputValue);
      }
    };
  };

  // Handle blur event to validate and add email
  const handleEmailBlur = (
    inputValue: string,
    setInputValue: (value: string) => void,
    emails: string[],
    setEmails: (emails: string[]) => void,
    isAddingRef: React.MutableRefObject<boolean>
  ) => {
    return () => {
      const trimmedValue = inputValue.trim();
      
      if (trimmedValue) {
        // Check if it's a valid email
        if (isValidEmail(trimmedValue) && !emails.includes(trimmedValue)) {
          // Mark as programmatic add, clear input, then add email
          isAddingRef.current = true;
          setInputValue('');
          setEmails([...emails, trimmedValue]);
        } else if (trimmedValue.includes(',')) {
          // Handle comma-separated emails
          const emailParts = trimmedValue.split(',').map(e => e.trim()).filter(e => e);
          const validEmails: string[] = [];
          
          emailParts.forEach(part => {
            if (isValidEmail(part) && !emails.includes(part) && !validEmails.includes(part)) {
              validEmails.push(part);
            }
          });
          
          if (validEmails.length > 0) {
            // Mark as programmatic add
            isAddingRef.current = true;
            // Keep only invalid/incomplete parts
            const invalidParts = emailParts.filter(part => !isValidEmail(part));
            setInputValue(invalidParts.join(', ') || '');
            // Add valid emails
            setEmails([...emails, ...validEmails]);
          } else {
            // Keep only invalid/incomplete parts
            const invalidParts = emailParts.filter(part => !isValidEmail(part));
            setInputValue(invalidParts.join(', ') || '');
          }
        }
      }
    };
  };

  if (!isOpen) return null;

  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 1,
        m: 0,
        border: `1px solid ${muiTheme.palette.divider}`,
        backgroundColor: muiTheme.palette.background.paper,
        height: '100%',
        maxHeight: '87vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Compose Header - theme-aware so "From" strip matches dark/light mode */}
      <Box sx={{
        px: 1.5,
        py: 1,
        borderBottom: `1px solid ${muiTheme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
        borderRadius: '4px 4px 0 0',
      }}>
        <Typography variant="body2" sx={{ 
          color: muiTheme.palette.text.primary,
          fontSize: '0.875rem'
        }}>
          <span style={{ fontWeight: 600 }}>From:</span> <span style={{ fontWeight: 400 }}>{extractedEmail || 'No email available'}</span>
        </Typography>
        <IconButton
          size="small"
          onClick={handleCloseWithConfirmation}
          sx={{ 
            color: muiTheme.palette.text.secondary,
            '&:hover': { 
              color: muiTheme.palette.text.primary,
              backgroundColor: muiTheme.palette.action.hover
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Compose Content */}
      <Box sx={{ 
        p: 1.5, 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'auto',
        minHeight: 0
      }}>
        {/* To Field */}
        {/* Email-specific fields (To, CC, BCC, Subject) - Only show for email channels */}
        {channelType === 'email' && (
          <Box sx={{ mb: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ 
              minWidth: '35px', 
              fontWeight: 500,
              color: muiTheme.palette.text.secondary,
              fontSize: '0.875rem'
            }}>
              To:
            </Typography>
          <Autocomplete
            multiple
            freeSolo
            value={toEmails}
            inputValue={toInputValue}
            onChange={handleEmailInput(toInputValue, setToInputValue, toEmails, setToEmails, isAddingToEmailRef)}
            onInputChange={handleEmailInputChange(toInputValue, setToInputValue, toEmails, setToEmails, isAddingToEmailRef, debouncedFetchEmailSuggestions)}
            options={emailSuggestions}
            filterOptions={(opts) => opts}
            filterSelectedOptions
            loading={emailSuggestionsLoading}
            sx={{ flex: 1, width: '100%' }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                  onDelete={() => {
                    const newEmails = toEmails.filter((_, i) => i !== index);
                    setToEmails(newEmails);
                  }}
                  sx={{
                    backgroundColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[700] : muiTheme.palette.grey[100],
                    color: muiTheme.palette.text.primary,
                    border: `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[300]}`,
                    '& .MuiChip-deleteIcon': {
                      color: muiTheme.palette.text.secondary,
                      '&:hover': {
                        color: muiTheme.palette.text.primary,
                        backgroundColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[200],
                      }
                    }
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Enter email addresses..."
                onFocus={() => fetchEmailSuggestions('')}
                onKeyDown={handleEmailKeyDown(toInputValue, setToInputValue, toEmails, setToEmails, isAddingToEmailRef)}
                onBlur={handleEmailBlur(toInputValue, setToInputValue, toEmails, setToEmails, isAddingToEmailRef)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {emailSuggestionsLoading ? <CircularProgress size={20} sx={{ mr: 0.5 }} /> : null}
                      {params.InputProps.endAdornment}
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setShowCc(!showCc)}
                          sx={{ 
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 0.5,
                            py: 0,
                            color: showCc ? muiTheme.palette.primary.main : muiTheme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor: 'transparent',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          Cc
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setShowBcc(!showBcc)}
                          sx={{ 
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 0.5,
                            py: 0,
                            color: showBcc ? muiTheme.palette.primary.main : muiTheme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor: 'transparent',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          Bcc
                        </Button>
                      </Box>
                    </>
                  ),
                }}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    padding: '4px 8px',
                    minHeight: '40px',
                    width: '100%',
                    '& fieldset': {
                      borderColor: muiTheme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: muiTheme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: muiTheme.palette.primary.main,
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 0',
                    fontSize: '0.875rem',
                  },
                }}
              />
            )}
          />
          </Box>
        )}

        {/* Cc Field */}
        {channelType === 'email' && showCc && (
          <Box sx={{ mb: 1.2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="body2" sx={{ 
              minWidth: '35px', 
              fontWeight: 500,
              color: muiTheme.palette.text.secondary,
              fontSize: '0.875rem',
              pt: 0.5
            }}>
              Cc:
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                multiple
                freeSolo
                value={ccEmails}
                inputValue={ccInputValue}
                onChange={handleEmailInput(ccInputValue, setCcInputValue, ccEmails, setCcEmails, isAddingCcEmailRef)}
                onInputChange={handleEmailInputChange(ccInputValue, setCcInputValue, ccEmails, setCcEmails, isAddingCcEmailRef, debouncedFetchEmailSuggestions)}
                options={emailSuggestions}
                filterOptions={(opts) => opts}
                filterSelectedOptions
                loading={emailSuggestionsLoading}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      onDelete={() => {
                        const newEmails = ccEmails.filter((_, i) => i !== index);
                        setCcEmails(newEmails);
                      }}
                      sx={{
                        backgroundColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[700] : muiTheme.palette.grey[100],
                        color: muiTheme.palette.text.primary,
                        border: `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[300]}`,
                        '& .MuiChip-deleteIcon': {
                          color: muiTheme.palette.text.secondary,
                          '&:hover': {
                            color: muiTheme.palette.text.primary,
                            backgroundColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[200],
                          }
                        }
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Enter CC email addresses..."
                    onFocus={() => fetchEmailSuggestions('')}
                    onKeyDown={handleEmailKeyDown(ccInputValue, setCcInputValue, ccEmails, setCcEmails, isAddingCcEmailRef)}
                    onBlur={handleEmailBlur(ccInputValue, setCcInputValue, ccEmails, setCcEmails, isAddingCcEmailRef)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {emailSuggestionsLoading ? <CircularProgress size={20} sx={{ mr: 0.5 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        padding: '4px 8px',
                        minHeight: '40px',
                        width: '100%',
                        '& fieldset': {
                          borderColor: muiTheme.palette.divider,
                        },
                        '&:hover fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 0',
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        )}

        {/* Bcc Field */}
        {channelType === 'email' && showBcc && (
          <Box sx={{ mb: 1.2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="body2" sx={{ 
              minWidth: '35px', 
              fontWeight: 500,
              color: muiTheme.palette.text.secondary,
              fontSize: '0.875rem',
              pt: 0.5
            }}>
              Bcc:
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                multiple
                freeSolo
                value={bccEmails}
                inputValue={bccInputValue}
                onChange={handleEmailInput(bccInputValue, setBccInputValue, bccEmails, setBccEmails, isAddingBccEmailRef)}
                onInputChange={handleEmailInputChange(bccInputValue, setBccInputValue, bccEmails, setBccEmails, isAddingBccEmailRef, debouncedFetchEmailSuggestions)}
                options={emailSuggestions}
                filterOptions={(opts) => opts}
                filterSelectedOptions
                loading={emailSuggestionsLoading}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      onDelete={() => {
                        const newEmails = bccEmails.filter((_, i) => i !== index);
                        setBccEmails(newEmails);
                      }}
                      sx={{
                        backgroundColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[700] : muiTheme.palette.grey[100],
                        color: muiTheme.palette.text.primary,
                        border: `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[300]}`,
                        '& .MuiChip-deleteIcon': {
                          color: muiTheme.palette.text.secondary,
                          '&:hover': {
                            color: muiTheme.palette.text.primary,
                            backgroundColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[200],
                          }
                        }
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Enter BCC email addresses..."
                    onFocus={() => fetchEmailSuggestions('')}
                    onKeyDown={handleEmailKeyDown(bccInputValue, setBccInputValue, bccEmails, setBccEmails, isAddingBccEmailRef)}
                    onBlur={handleEmailBlur(bccInputValue, setBccInputValue, bccEmails, setBccEmails, isAddingBccEmailRef)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {emailSuggestionsLoading ? <CircularProgress size={20} sx={{ mr: 0.5 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        padding: '4px 8px',
                        minHeight: '40px',
                        width: '100%',
                        '& fieldset': {
                          borderColor: muiTheme.palette.divider,
                        },
                        '&:hover fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 0',
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        )}

        {/* Subject Line */}
        {channelType === 'email' && (
          <Box sx={{ mb: 1.2 }}>
            <TextField
              fullWidth
            variant="outlined"
            size="small"
            placeholder="Add a subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            InputProps={{
              readOnly: isReplyOrForward,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: '4px 8px',
                minHeight: '40px',
                '& fieldset': {
                  borderColor: muiTheme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: isReplyOrForward ? muiTheme.palette.divider : muiTheme.palette.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: isReplyOrForward ? muiTheme.palette.divider : muiTheme.palette.primary.main,
                  borderWidth: isReplyOrForward ? '1px' : '2px',
                },
              },
              '& .MuiInputBase-input': {
                padding: '8px 0',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: isReplyOrForward ? 'default' : 'text',
                color: isReplyOrForward ? muiTheme.palette.text.secondary : muiTheme.palette.text.primary,
              },
            }}
          />
          </Box>
        )}

        {/* Hidden File Input */}
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Message Content - Fixed Height */}
        <Box>
          <RichTextEditor
            key={editorKey}
            editable={true}
            renderControls={() => (
              <MenuControlsContainer>
                <MenuButtonBold />
                <MenuButtonItalic />
                <MenuButtonUnderline />
                <MenuButtonStrikethrough />
                <MenuDivider />
                <MenuButtonBulletedList />
                <MenuButtonOrderedList />
                <MenuButtonBlockquote />
                <MenuDivider />
                <MenuButtonUndo />
                <MenuButtonRedo />
                {/* Generate Draft Button - Only show for Reply and Reply All */}
                {isReplyOrForward && !isForward && onGenerateDraft && (
                  <>
                    <MenuDivider />
                    <MenuButton
                      value="generate-draft"
                      tooltipLabel={isGeneratingDraft ? (draftStatusMessage || 'Generating draft...') : 'Generate Draft'}
                      onClick={isGeneratingDraft ? undefined : onGenerateDraft}
                      disabled={isGeneratingDraft && draftStatusType !== 'success' && draftStatusType !== 'error'}
                      sx={{
                        minWidth: 'auto',
                        padding: isGeneratingDraft ? '4px 8px !important' : '2px 8px !important',
                        fontSize: isGeneratingDraft ? '13px' : '0.75rem',
                        fontWeight: isGeneratingDraft ? 400 : 600,
                        textTransform: 'none',
                        color: isGeneratingDraft
                          ? draftStatusType === 'loading'
                            ? muiTheme.palette.text.primary // Dark text for loading
                            : '#fff' // White text for success/error
                          : muiTheme.palette.primary.main,
                        backgroundColor: isGeneratingDraft
                          ? draftStatusType === 'error'
                            ? '#f44336 !important' // Toast error color
                            : draftStatusType === 'success'
                            ? '#4caf50 !important' // Toast success color
                            : muiTheme.palette.mode === 'dark'
                            ? 'rgba(25, 118, 210, 0.2) !important' // Subtle blue for dark mode
                            : 'rgba(25, 118, 210, 0.08) !important' // Subtle blue for light mode
                          : `${muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[700] : muiTheme.palette.grey[100]} !important`,
                        border: isGeneratingDraft
                          ? 'none !important' // No border for toast-like appearance
                          : `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[300]} !important`,
                        borderRadius: isGeneratingDraft ? '8px' : '6px', // Toast uses 8px border radius
                        height: '36px',
                        minHeight: '36px',
                        maxHeight: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isGeneratingDraft && draftStatusType !== 'success' && draftStatusType !== 'error' ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: isGeneratingDraft 
                          ? 'none !important' // No shadow for draft status banner
                          : '0 1px 2px rgba(0,0,0,0.1)',
                        '&:hover': {
                          ...(isGeneratingDraft && draftStatusType !== 'success' && draftStatusType !== 'error' ? {} : {
                            backgroundColor: isGeneratingDraft
                              ? draftStatusType === 'error'
                                ? '#d32f2f !important' // Darker red on hover
                                : draftStatusType === 'success'
                                ? '#388e3c !important' // Darker green on hover
                                : muiTheme.palette.mode === 'dark'
                                ? 'rgba(25, 118, 210, 0.3) !important' // Slightly darker blue on hover
                                : 'rgba(25, 118, 210, 0.12) !important' // Slightly darker blue on hover
                              : `${muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[200]} !important`,
                            boxShadow: isGeneratingDraft
                              ? 'none !important'
                              : '0 2px 4px rgba(0,0,0,0.15)',
                            transform: 'translateY(-1px)',
                          }),
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                          boxShadow: isGeneratingDraft
                            ? 'none !important'
                            : '0 1px 2px rgba(0,0,0,0.1)',
                        },
                        '&:disabled': {
                          color: isGeneratingDraft && draftStatusType === 'loading'
                            ? muiTheme.palette.text.primary
                            : '#fff',
                          backgroundColor: isGeneratingDraft
                            ? draftStatusType === 'error'
                              ? '#f44336 !important'
                              : draftStatusType === 'success'
                              ? '#4caf50 !important'
                              : muiTheme.palette.mode === 'dark'
                              ? 'rgba(25, 118, 210, 0.2) !important'
                              : 'rgba(25, 118, 210, 0.08) !important'
                            : `${muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50]} !important`,
                          border: 'none',
                          cursor: 'not-allowed',
                          transform: 'none',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {isGeneratingDraft ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5, 
                          lineHeight: 1 
                        }}>
                          {draftStatusType === 'error' ? (
                            <ErrorIcon sx={{ fontSize: '16px', color: '#fff', flexShrink: 0 }} />
                          ) : draftStatusType === 'success' ? (
                            <CheckCircleIcon sx={{ fontSize: '16px', color: '#fff', flexShrink: 0 }} />
                          ) : (
                            <CircularProgress 
                              size={14} 
                              thickness={4} 
                              sx={{ 
                                color: muiTheme.palette.info.main, 
                                flexShrink: 0 
                              }} 
                            />
                          )}
                          <Typography
                            sx={{
                              textTransform: 'none',
                              fontWeight: 400,
                              fontSize: '13px',
                              color: draftStatusType === 'loading'
                                ? muiTheme.palette.text.primary
                                : '#fff',
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineHeight: 1.2,
                              flex: 1,
                              minWidth: 0
                            }}
                          >
                            {draftStatusMessage || (draftStatusType === 'success' ? 'Draft generated successfully!' : draftStatusType === 'error' ? 'Draft generation failed' : 'Composing the reply draft...')}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5, 
                          textTransform: 'none !important',
                          '& *': {
                            textTransform: 'none !important'
                          }
                        }}>
                          <GenerateIcon sx={{ fontSize: '0.875rem' }} />
                          <span style={{ textTransform: 'none', fontWeight: 700 }}>Generate Draft</span>
                        </Box>
                      )}
                    </MenuButton>
                  </>
                )}
              </MenuControlsContainer>
            )}
            content={composeContent}
            onUpdate={({ editor }) => {
              const html = editor.getHTML();
              setComposeContent(html);
            }}
            onCreate={({ editor }) => {
              // Store editor reference for programmatic content updates
              editorRef.current = editor;
              // Defer any view.dom access: Tiptap view is not available until after mount (longer delays for Strict Mode / slow mount)
              const runAfterMount = (fn: () => void, delayMs: number) => {
                setTimeout(() => {
                  try {
                    fn();
                  } catch (_) {
                    // Editor view may not be mounted yet; ignore
                  }
                }, delayMs);
              };
              // Set font size once view is mounted
              runAfterMount(() => {
                const el = (editor as any).view?.dom;
                if (el) el.style.fontSize = '0.875rem';
              }, 400);
              // Set initial content once view is mounted
              runAfterMount(() => {
                if (initialData?.content && editor.getHTML() !== initialData.content) {
                  editor.commands.setContent(initialData.content, { emitUpdate: false });
                  setComposeContent(initialData.content);
                }
              }, 400);
              // Auto-focus for reply/forward once view is mounted (delay so view.dom is available)
              if (isReplyOrForward) {
                runAfterMount(() => editor.commands.focus('start'), 800);
              }
            }}
            onFocus={() => setIsEditorFocused(true)}
            onBlur={() => setIsEditorFocused(false)}
            extensions={[
              CollapsibleContent,
              StarterKit.configure({
                heading: false,
                codeBlock: false,
                blockquote: false,
                code: false,
                underline: false,
                // Gmail-style spacing: no margin between paragraphs, normal line height
                paragraph: {
                  HTMLAttributes: {
                    style: 'margin: 0; line-height: 1.5;',
                  },
                },
              }),
              Underline,
              Blockquote.configure({
                HTMLAttributes: {
                  class: 'email-blockquote',
                },
              }),
              Mention.configure(mentionSuggestionOptions),
              Placeholder.configure({
                placeholder: 'Compose your message here...',
              }),
            ]}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              '& .MuiOutlinedInput-root': {
                border: 'none',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' },
                backgroundColor: 'transparent',
                fontSize: '0.875rem',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              },
              '& .MuiTiptap-RichTextField-menuBarContent': {
                padding: '4px 8px',
              },
              // Override sticky positioning for menu bar - make it scroll with content
              '& .MuiTiptap-RichTextField-menuBar': {
                position: 'static !important',
              },
              '& .MuiTiptap-RichTextField-menuBarWrapper': {
                position: 'static !important',
              },
              '& [class*="MuiTiptap-RichTextField-menuBar"]': {
                position: 'static !important',
              },
              // Target any element with sticky positioning within the editor and make it static
              '& [class*="menuBar"], & [class*="MenuBar"], & [class*="menu-bar"]': {
                position: 'static !important',
              },
              '& .ProseMirror': {
                padding: '16px 0px',
                fontSize: '0.875rem !important',
                lineHeight: 1.5,
                minHeight: '200px',
                fontFamily: 'inherit',
                '& p': {
                  fontSize: '0.875rem !important',
                  margin: 0,
                  lineHeight: 1.5,
                },
                '& ul, & ol': {
                  fontSize: '0.875rem !important',
                },
                '& li': {
                  fontSize: '0.875rem !important',
                },
                '& strong, & b': {
                  fontSize: '0.875rem !important',
                },
                '& em, & i': {
                  fontSize: '0.875rem !important',
                },
                // Gmail-style collapsible content styling
                '& .collapsible-content-wrapper': {
                  margin: '0',
                  display: 'block',
                  fontFamily: `Roboto, Helvetica, Arial, sans-serif`,
                },
                // Regular blockquote styling for manual blockquotes
                '& blockquote': {
                  margin: '16px 0',
                  padding: '12px 16px',
                  borderLeft: `3px solid ${muiTheme.palette.primary.main}`,
                  backgroundColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                  fontStyle: 'italic',
                  color: muiTheme.palette.text.secondary,
                  borderRadius: '0 4px 4px 0',
                },
                // Override default paragraph margins for Gmail-style content
                '& .collapsible-content-wrapper p': {
                  margin: '0 0 4px 0 !important',
                  lineHeight: '1.4 !important',
                  fontSize: '13px !important',
                  fontFamily: 'arial, sans-serif !important',
                },
                '& .collapsible-content-wrapper div': {
                  margin: '0 !important',
                  lineHeight: '1.4 !important',
                  fontSize: '13px !important',
                  fontFamily: 'arial, sans-serif !important',
                },
              }
            }}
          />
        </Box>

        {/* Sources (citations) from generate draft - below editor */}
        {draftCitations && draftCitations.length > 0 && (
          <CitationsDisplay citations={draftCitations} />
        )}

        {/* Confidence score and token usage from generate draft - below Sources */}
        <DraftUsageMetrics usageMetrics={draftUsageMetrics} />

        {/* Attachments List - Below Editor, Above Action Buttons */}
        {attachments.length > 0 && (
          <Box sx={{ mb: 1.2, mt: 1 }}>
            {attachments.map((attachment, index) => (
              <Chip
                key={index}
                icon={
                  attachment.uploadStatus === 'uploading' ? (
                    <CircularProgress size={16} />
                  ) : attachment.uploadStatus === 'success' ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: muiTheme.palette.success.main }} />
                  ) : attachment.uploadStatus === 'error' ? (
                    <ErrorIcon sx={{ fontSize: 16, color: muiTheme.palette.error.main }} />
                  ) : (
                    <InsertDriveFileIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {attachment.filename || attachment.file?.name || 'Unknown file'}
                    </Typography>
                    {attachment.file_size && (
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: muiTheme.palette.text.secondary }}>
                        ({formatFileSize(attachment.file_size)})
                      </Typography>
                    )}
                    {attachment.error && (
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: muiTheme.palette.error.main }}>
                        - {attachment.error}
                      </Typography>
                    )}
                  </Box>
                }
                onDelete={() => handleRemoveAttachment(index)}
                sx={{
                  mb: 0.5,
                  mr: 0.5,
                  backgroundColor:
                    attachment.uploadStatus === 'error'
                      ? muiTheme.palette.error.light
                      : attachment.uploadStatus === 'success'
                      ? muiTheme.palette.success.light
                      : muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[700] : muiTheme.palette.grey[100],
                  '& .MuiChip-deleteIcon': {
                    color: muiTheme.palette.text.secondary,
                    '&:hover': {
                      color: muiTheme.palette.error.main,
                    }
                  }
                }}
              />
            ))}
          </Box>
        )}

        {/* Action Buttons - Fixed at Bottom */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pt: 1,
          // borderTop: `1px solid ${muiTheme.palette.divider}`,
          mt: 'auto',
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <IconButton 
              size="small" 
              onClick={handleAttachClick}
              disabled={isSending}
              sx={{ 
                color: muiTheme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: muiTheme.palette.action.hover,
                  color: muiTheme.palette.text.primary
                },
                '&:disabled': {
                  color: muiTheme.palette.action.disabled
                }
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2,
                py: 0.5,
                borderColor: muiTheme.palette.divider,
                color: muiTheme.palette.text.secondary,
                '&:hover': {
                  borderColor: muiTheme.palette.text.primary,
                  color: muiTheme.palette.text.primary,
                  backgroundColor: muiTheme.palette.action.hover
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleSend}
              disabled={!composeContent.replace(/<[^>]*>/g, '').trim() || isSending}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2,
                py: 0.5,
                backgroundColor: muiTheme.palette.primary.main,
                '&:hover': {
                  backgroundColor: muiTheme.palette.primary.dark,
                },
                '&:disabled': {
                  backgroundColor: muiTheme.palette.action.disabled,
                  color: muiTheme.palette.action.disabled,
                }
              }}
            >
              {isSending ? 'Sending...' : 'Send'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Close Confirmation Dialog */}
      <Dialog
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        aria-labelledby="close-confirmation-title"
        aria-describedby="close-confirmation-description"
      >
        <DialogTitle id="close-confirmation-title">
          Discard Changes?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="close-confirmation-description">
            You have unsaved changes. All typed text will be cleared if you close the compose box. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color="primary">
            Keep Editing
          </Button>
          <Button onClick={handleConfirmClose} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Attachment Restriction Modal */}
      <ChannelRestrictionModal
        open={openAttachmentRestrictionModal}
        onClose={() => setOpenAttachmentRestrictionModal(false)}
        restrictionType="attachment"
      />
    </Paper>
  );
};

export default ComposeBox;
